'use client'

import React, { type ComponentProps } from 'react'
import { type DateRange } from 'react-day-picker'
import { cx } from 'class-variance-authority'
import { CalendarDotsIcon } from '@phosphor-icons/react/dist/ssr/CalendarDots'
import type { SupportedLanguages } from '@skop-app/lib-domain/types'

import { Button } from '@/components/atoms/Button/button.atom'
import { DropdownMenu } from '@/components/atoms/DropdownMenu/dropdownMenu.atom'
import {
  Calendar,
  type CalendarAccentColors,
} from '@/components/atoms/Calendar/calendar.atom'
import { Select } from '@/components/atoms/Select/select.atom'
import {
  PeriodicCalendar,
  type PeriodOption,
} from '@/components/atoms/PeriodicCalendar/periodicCalendar.atom'
import {
  foregroundForPrimaryBackground,
  reactSelectTheme,
  resolveReactSelectAccentColors,
} from '../Select/select.config'

const toCalendarAccentColors = (
  resolved: ReturnType<typeof resolveReactSelectAccentColors>,
): CalendarAccentColors => {
  return {
    primary: resolved.primary,
    primary25: resolved.primary25,
    primary50: resolved.primary50,
    primary75: resolved.primary75,
    onPrimary: resolved.onPrimary,
    onPrimary25: foregroundForPrimaryBackground(resolved.primary25),
  }
}

export type DateRangePickerReactSelectStyles = NonNullable<
  ComponentProps<typeof Select>['styles']
>

export type DateBoundFilterMode = 'range' | 'after' | 'before'

export type DateRangeFilterValue = DateRange & {
  mode?: DateBoundFilterMode
}

const hasFilterSelection = (
  value: DateRangeFilterValue | undefined,
): boolean => {
  if (!value) return false
  if (value.mode === 'after') return Boolean(value.from)
  if (value.mode === 'before') return Boolean(value.to)
  return Boolean(value.from)
}

const getBoundModeLabels = (
  language: SupportedLanguages,
): Record<DateBoundFilterMode, string> => {
  switch (language) {
    case 'fr':
      return { range: 'Plage', after: 'Après', before: 'Avant' }
    case 'nl':
      return { range: 'Bereik', after: 'Na', before: 'Voor' }
    default:
      return { range: 'Range', after: 'After', before: 'Before' }
  }
}

const getBoundPrefixLabels = (
  language: SupportedLanguages,
): {
  after: string
  before: string
} => {
  switch (language) {
    case 'fr':
      return { after: 'Après le', before: 'Avant le' }
    case 'nl':
      return { after: 'Na', before: 'Voor' }
    default:
      return { after: 'After', before: 'Before' }
  }
}

const getLocaleForLanguage = (language: SupportedLanguages): string => {
  switch (language) {
    case 'fr':
      return 'fr-FR'
    case 'nl':
      return 'nl-NL'
    default:
      return 'en-US'
  }
}

const getDateFilterModeAriaLabel = (language: SupportedLanguages): string => {
  switch (language) {
    case 'fr':
      return 'Mode de comparaison des dates'
    case 'nl':
      return 'Datumvergelijkingsmodus'
    default:
      return 'Date comparison mode'
  }
}

export type DateRangePickerProps = {
  buttonLabel?: React.ReactNode
  defaultLabel?: string
  buttonIcon?: React.ReactNode
  value?: DateRangeFilterValue
  onChange?: (range: DateRangeFilterValue | undefined) => void
  label?: string
  dateFormat?: (date: Date) => string
  className?: string
  children?: React.ReactNode
  isPeriodicCalendar?: boolean
  isNormalCalendar?: boolean
  isDisabled?: boolean
  periodCalendarClassName?: string
  normalCalendarClassName?: string
  language?: SupportedLanguages
  buttonClassName?: string
  showResetButton?: boolean
  reactSelectTheme?: typeof reactSelectTheme
  reactSelectStyles?: DateRangePickerReactSelectStyles
  align?: 'start' | 'center' | 'end'
  labelAsText?: boolean
  allowOpenBounds?: boolean
}

export const DateRangePicker = (props: DateRangePickerProps) => {
  const {
    buttonLabel,
    defaultLabel,
    buttonIcon,
    value,
    onChange,
    label,
    dateFormat,
    children,
    isPeriodicCalendar = true,
    isNormalCalendar = true,
    isDisabled = false,
    className,
    periodCalendarClassName,
    normalCalendarClassName,
    language = 'fr',
    buttonClassName,
    showResetButton = true,
    reactSelectTheme,
    reactSelectStyles,
    align = 'end',
    labelAsText = false,
    allowOpenBounds = false,
  } = props

  const [isOpen, setIsOpen] = React.useState(false)
  const [selectedPeriod, setSelectedPeriod] = React.useState<
    PeriodOption | undefined
  >()
  const [hasEverSelected, setHasEverSelected] = React.useState(false)
  const [boundMode, setBoundMode] = React.useState<DateBoundFilterMode>(
    () => value?.mode ?? 'range',
  )

  const prevValueRef = React.useRef(value)
  React.useEffect(() => {
    if (prevValueRef.current !== value) {
      prevValueRef.current = value
      setBoundMode(value?.mode ?? 'range')
    }
  }, [value])

  React.useEffect(() => {
    if (hasFilterSelection(value)) {
      setHasEverSelected(true)
    }
  }, [value])

  const formatDate = React.useCallback(
    (date: Date | undefined) => {
      if (!date) return ''
      if (dateFormat) return dateFormat(date)
      return date.toLocaleDateString(getLocaleForLanguage(language), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
    [dateFormat, language],
  )

  const boundLabels = getBoundPrefixLabels(language)

  const getButtonLabel = () => {
    if (children) return children
    if (labelAsText && selectedPeriod && selectedPeriod.value !== 'custom') {
      return selectedPeriod[language]
    }
    if (value?.mode === 'after' && value.from) {
      return `${boundLabels.after} ${formatDate(value.from)}`
    }
    if (value?.mode === 'before' && value.to) {
      return `${boundLabels.before} ${formatDate(value.to)}`
    }
    if (value?.from) {
      if (value.to) {
        return `${formatDate(value.from)} - ${formatDate(value.to)}`
      }
      return formatDate(value.from)
    }
    if (defaultLabel && !hasEverSelected) {
      return defaultLabel
    }
    return label || defaultLabel || 'Select date'
  }

  const handleBoundModeClick = (next: DateBoundFilterMode) => {
    setBoundMode(next)
    if (!allowOpenBounds || !onChange) return
    const customPeriod: PeriodOption = {
      value: 'custom',
      fr: 'Période personnalisée',
      en: 'Custom period',
      nl: 'Aangepaste periode',
    }

    const v = value
    if (next === 'range') {
      if (v?.mode === 'after' && v.from) {
        onChange({ from: v.from, to: v.from })
        setSelectedPeriod(customPeriod)
      } else if (v?.mode === 'before' && v.to) {
        onChange({ from: v.to, to: v.to })
        setSelectedPeriod(customPeriod)
      }
      return
    }
    if (next === 'after') {
      if (v?.mode === 'before' && v.to) {
        onChange({ from: v.to, to: undefined, mode: 'after' })
        setSelectedPeriod(customPeriod)
      } else if ((v?.mode === 'range' || !v?.mode) && v?.from) {
        onChange({ from: v.from, to: undefined, mode: 'after' })
        setSelectedPeriod(customPeriod)
      }
      return
    }
    if (next === 'before') {
      if (v?.mode === 'after' && v.from) {
        onChange({ from: undefined, to: v.from, mode: 'before' })
        setSelectedPeriod(customPeriod)
      } else if ((v?.mode === 'range' || !v?.mode) && v?.to) {
        onChange({ from: undefined, to: v.to, mode: 'before' })
        setSelectedPeriod(customPeriod)
      } else if ((v?.mode === 'range' || !v?.mode) && v?.from && !v?.to) {
        onChange({ from: undefined, to: v.from, mode: 'before' })
        setSelectedPeriod(customPeriod)
      }
    }
  }

  const handleSelectRange = (range: DateRange | undefined) => {
    if (!range?.from) {
      onChange?.(undefined)
      setSelectedPeriod(undefined)
      return
    }
    const next: DateRangeFilterValue = {
      from: range.from,
      to: range.to,
    }
    onChange?.(next)
    setHasEverSelected(true)
    setSelectedPeriod({
      value: 'custom',
      fr: 'Période personnalisée',
      en: 'Custom period',
      nl: 'Aangepaste periode',
    })
  }

  const handleSelectSingle = (date: Date | undefined) => {
    if (!date) {
      onChange?.(undefined)
      setSelectedPeriod(undefined)
      return
    }
    setHasEverSelected(true)
    setSelectedPeriod({
      value: 'custom',
      fr: 'Période personnalisée',
      en: 'Custom period',
      nl: 'Aangepaste periode',
    })
    if (boundMode === 'after') {
      onChange?.({ from: date, to: undefined, mode: 'after' })
    } else if (boundMode === 'before') {
      onChange?.({ from: undefined, to: date, mode: 'before' })
    }
  }

  const handleReset = () => {
    onChange?.(undefined)
    setSelectedPeriod(undefined)
    setIsOpen(false)
  }

  const getResetButtonText = (language: SupportedLanguages) => {
    switch (language) {
      case 'fr':
        return 'Réinitialiser'
      case 'nl':
        return 'Resetten'
      default:
        return 'Reset'
    }
  }

  const handlePeriodSelect = (option: PeriodOption | null) => {
    if (!option) {
      onChange?.(undefined)
      setSelectedPeriod(undefined)
      return
    }

    setSelectedPeriod(option)
    setHasEverSelected(true)

    if (option.value === 'custom') {
      return
    }

    const now = new Date()
    let from: Date
    let to: Date = now

    switch (option.value) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
      case 'lastWeek': {
        const currentDay = now.getDay()
        const daysToMonday = currentDay === 1 ? 6 : currentDay - 1
        const startOfCurrentWeek = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - daysToMonday,
        )

        from = new Date(
          startOfCurrentWeek.getFullYear(),
          startOfCurrentWeek.getMonth(),
          startOfCurrentWeek.getDate() - 7,
        )
        to = new Date(
          startOfCurrentWeek.getFullYear(),
          startOfCurrentWeek.getMonth(),
          startOfCurrentWeek.getDate() - 1,
        )
        break
      }
      case 'currentWeek': {
        const currentDay = now.getDay()
        const daysToMonday = currentDay === 0 ? 6 : currentDay - 1
        const startOfCurrentWeek = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - daysToMonday,
        )

        from = startOfCurrentWeek
        to = new Date(
          startOfCurrentWeek.getFullYear(),
          startOfCurrentWeek.getMonth(),
          startOfCurrentWeek.getDate() + 6,
        )
        break
      }
      case 'lastMonth': {
        const currentMonth = now.getMonth()
        const currentYear = now.getFullYear()
        const lastMonth = currentMonth - 1
        const lastYear = currentYear
        from = new Date(lastYear, lastMonth, 1)
        to = new Date(lastYear, lastMonth + 1, 0)
        break
      }
      case 'month':
        from = new Date(now.getFullYear(), now.getMonth(), 1)
        break
      case 'year':
        from = new Date(now.getFullYear(), 0, 1)
        break
      case 'lastYear':
        from = new Date(now.getFullYear() - 1, 0, 1)
        to = new Date(now.getFullYear() - 1, 11, 31)
        break
      case 'fiveYears':
        from = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate())
        break
      default:
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        break
    }

    onChange?.({ from, to })
  }

  const modeToggleLabels = getBoundModeLabels(language)
  const showOpenBoundsToggle = allowOpenBounds && isNormalCalendar
  const calendarUsesRange = !showOpenBoundsToggle || boundMode === 'range'
  const singleSelected =
    boundMode === 'after'
      ? value?.from
      : boundMode === 'before'
        ? value?.to
        : undefined

  const unifiedDatePanel = showOpenBoundsToggle
  const showCalendarResetFooter =
    isNormalCalendar &&
    hasFilterSelection(value) &&
    showResetButton &&
    unifiedDatePanel

  const calendarClassName = cx(
    unifiedDatePanel && 'rounded-none !border-0 !shadow-none bg-white',
    normalCalendarClassName,
  )

  const calendarNavProps = {
    captionLayout: 'dropdown' as const,
    fromYear: new Date().getFullYear() - 10,
    toYear: new Date().getFullYear() + 10,
  }

  const calendarAccentColors = React.useMemo(
    () =>
      toCalendarAccentColors(resolveReactSelectAccentColors(reactSelectTheme)),
    [reactSelectTheme],
  )

  const calendarNode = calendarUsesRange ? (
    <Calendar.Root
      mode='range'
      selected={value}
      onSelect={handleSelectRange}
      numberOfMonths={2}
      disabled={isDisabled}
      className={calendarClassName}
      language={language}
      accentColors={calendarAccentColors}
      {...calendarNavProps}
    />
  ) : (
    <Calendar.Root
      mode='single'
      selected={singleSelected}
      onSelect={handleSelectSingle}
      numberOfMonths={2}
      disabled={isDisabled}
      className={calendarClassName}
      language={language}
      accentColors={calendarAccentColors}
      {...calendarNavProps}
    />
  )

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenu.Trigger
        className={cx(
          'flex items-center justify-between gap-2 !ring-0 !focus-visible:ring-0 !focus:ring-0 !focus:ring-offset-0 !focus-visible:ring-offset-0 !outline-none',
          buttonClassName,
        )}
      >
        <span className='truncate'>
          {labelAsText && selectedPeriod && selectedPeriod.value !== 'custom'
            ? getButtonLabel()
            : buttonLabel && !hasFilterSelection(value)
              ? buttonLabel
              : getButtonLabel()}
        </span>
        {buttonIcon || (
          <CalendarDotsIcon className='size-4 text-slate-300 shrink-0' />
        )}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        align={align}
        className='border-0 shadow-none p-0 !bg-transparent'
      >
        <div className={cx('flex flex-col gap-2 p-2', className)}>
          {isPeriodicCalendar && !unifiedDatePanel && (
            <PeriodicCalendar
              value={selectedPeriod}
              onChange={handlePeriodSelect}
              isDisabled={isDisabled}
              className={periodCalendarClassName}
              language={language}
              reactSelectTheme={reactSelectTheme}
              reactSelectStyles={reactSelectStyles}
            />
          )}
          {isNormalCalendar && unifiedDatePanel && (
            <div className='flex flex-col overflow-hidden rounded-xl border border-zinc-250 bg-white shadow-[0_4px_20px_-4px_rgba(15,23,42,0.07),0_2px_6px_-2px_rgba(15,23,42,0.04)]'>
              <div className='border-b border-zinc-250 bg-zinc-100 px-3 pt-3 pb-2.5'>
                <div
                  role='tablist'
                  aria-label={getDateFilterModeAriaLabel(language)}
                  className='grid w-full grid-cols-3 gap-0.5 rounded-lg bg-zinc-200 p-0.5'
                >
                  {(
                    ['range', 'after', 'before'] satisfies DateBoundFilterMode[]
                  ).map((m) => (
                    <button
                      key={m}
                      type='button'
                      role='tab'
                      aria-selected={boundMode === m}
                      disabled={isDisabled}
                      onClick={() => handleBoundModeClick(m)}
                      className={cx(
                        'flex min-h-9 items-center justify-center rounded-[7px] px-1.5 py-2 text-center text-xs font-medium leading-tight transition-colors duration-150',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300/25 focus-visible:ring-offset-1 focus-visible:ring-offset-zinc-50/80',
                        'disabled:pointer-events-none disabled:opacity-50',
                        boundMode === m
                          ? 'bg-white text-zinc-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ring-1 ring-zinc-200/40'
                          : 'text-zinc-500 hover:bg-white/50 hover:text-zinc-700',
                      )}
                    >
                      {modeToggleLabels[m]}
                    </button>
                  ))}
                </div>
              </div>
              {isPeriodicCalendar && boundMode === 'range' && (
                <div className='border-b border-zinc-250 bg-white px-3 py-3'>
                  <PeriodicCalendar
                    value={selectedPeriod}
                    onChange={handlePeriodSelect}
                    isDisabled={isDisabled}
                    className={periodCalendarClassName}
                    language={language}
                    reactSelectTheme={reactSelectTheme}
                    reactSelectStyles={reactSelectStyles}
                  />
                </div>
              )}
              {calendarNode}
              {showCalendarResetFooter && (
                <div className='flex justify-end border-t border-zinc-250 bg-zinc-50/30 px-3 py-2'>
                  <Button
                    variant='ghost'
                    type='button'
                    size='sm'
                    className='h-8 text-xs font-medium text-zinc-500 hover:bg-zinc-100/50 hover:text-zinc-800'
                    onClick={handleReset}
                    disabled={isDisabled}
                  >
                    {getResetButtonText(language)}
                  </Button>
                </div>
              )}
            </div>
          )}
          {isNormalCalendar && !unifiedDatePanel && calendarNode}
          {isNormalCalendar &&
            !unifiedDatePanel &&
            hasFilterSelection(value) &&
            showResetButton && (
              <div className='flex w-full justify-end'>
                <Button
                  variant='ghost'
                  type='button'
                  size='sm'
                  className='rounded-md bg-white text-sm'
                  onClick={handleReset}
                  disabled={isDisabled}
                >
                  {getResetButtonText(language)}
                </Button>
              </div>
            )}
        </div>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

DateRangePicker.displayName = 'DateRangePicker'
